import React, { memo, useEffect, useMemo, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { useEffectOnce } from 'react-use'
import { ControlledSelect } from 'transfix-form/components'
import { Spinner } from 'transfix-ui/components/Spinner'
import { Switch } from 'transfix-ui/components/Switch'
import { Table, TableBody, TableCell, TableHead, TableRow } from 'transfix-ui/components/Table'
import { Box } from 'transfix-ui/core'
import { IConnectionTypes, IEdiMessage, IFtpConnectionTypes, Maybe } from 'types/graphqlTypes'
import { StringParam } from 'use-query-params'
import { useQueryParams } from 'utils/hooks'
import { useEdiMessageType } from '../../ConnectionConfigs/EdiRoutingTable/hooks/useInboundEdiMessageTypeWithConfig'
import { InboundConnectionConfigDetails } from '../../ConnectionConfigs/InboundConnectionConfig/InboundConnectionConfigDetails'
import { useEdiInboundConnectionConfig } from '../../ConnectionConfigs/InboundConnectionConfig/hooks/useEdiInboundConnectionConfig'
import { TAddInboundConnectionConfigModalSchema } from '../../ConnectionConfigs/InboundConnectionConfig/schema/AddInboundConnectionConfigModalSchema'
import { EDI_TOOLS_ROUTES } from '../../LandingPage'
import { INBOUND_TAB } from '../constants'
import { useShipperConfigForm } from '../hooks/useShipperConfigForm'
import { useTransactionSets } from '../hooks/useTransactionSets'
import { ParserSideBar } from './ParserSideBar'
import { ShipperConfigInboundRow } from './ShipperConfigInboundRow'

interface IInboundIntegrationDetailsProps {
  inboundConfigIds: Maybe<string | string[]>
}

export const InboundIntegrationDetails = memo<IInboundIntegrationDetailsProps>(
  ({ inboundConfigIds }) => {
    const [{ tab }, setQueryParams] = useQueryParams({
      tab: StringParam,
    })

    const {
      ediMessageTypes: inboundEdiMessageTypes,
      loading: inboundMessageTypeLoading,
    } = useEdiMessageType({
      connectionConfigType: 'InboundConnectionConfig',
      connectionConfigIds: inboundConfigIds,
    })

    const history = useHistory()
    const { configId, id, name, parserId } = useParams<{
      id: string
      name: string
      configId: string
      parserId: string
    }>()

    const sortedEdiMessageTypes = useMemo(
      () =>
        inboundEdiMessageTypes.sort((a, b) => {
          return Number(b?.ediMapperConnectionConfig?.id) - Number(a?.ediMapperConnectionConfig?.id)
        }),
      [inboundEdiMessageTypes]
    )
    const [isConfigSidebarOpen, setIsConfigSidebarOpen] = useState(Boolean(configId))
    const [isParserSidebarOpen, setIsParserSidebarOpen] = useState(Boolean(parserId))
    const [showOnlyActive, setShowOnlyActive] = useState<boolean>(false)
    const [selectedTransactionSet, setSelectedTransactionSet] = useState<string>('none')

    const {
      updateAs2InboundConnectionConfig,
      updateFtpInboundConnectionConfig,
    } = useEdiInboundConnectionConfig()

    const submitHandler = (data: TAddInboundConnectionConfigModalSchema) => {
      const { connectionType } = data

      if (connectionType === IConnectionTypes.AS2) {
        updateAs2InboundConnectionConfig({
          id: data?.id,
          as2ConnectionConfigId: data?.as2ConnectionConfigId,
          disabled: data?.disabled,
          environment: data?.environment,
          filenameGlob: data?.filenameGlob,
          partnerApplicationId: data?.company.id,
          partnerApplicationType: data?.company.data.__typename,
        })
      } else {
        let ftpConnectionType: IFtpConnectionTypes

        if (connectionType === 'SFTP_WITH_PASSWORD') {
          ftpConnectionType = IFtpConnectionTypes.SFTP_WITH_PASSWORD
        } else if (connectionType === 'SFTP_WITH_PRIVATE_KEY') {
          ftpConnectionType = IFtpConnectionTypes.SFTP_WITH_PRIVATE_KEY
        } else {
          ftpConnectionType = IFtpConnectionTypes.FTP
        }

        updateFtpInboundConnectionConfig({
          id: data?.id,
          connectionType: ftpConnectionType,
          disabled: data?.disabled,
          environment: data?.environment,
          filenameGlob: data?.filenameGlob,
          partnerApplicationId: data?.company.id,
          partnerApplicationType: data?.company.data.__typename,
          username: data?.username,
          host: data?.host,
          port: data?.port,
          pathPrefix: data?.pathPrefix,
          secretKey: data?.secretKey,
        })
      }
    }

    /*
     * When URL param configId or parserId updates, it sets new state of Sidebar panel
     */
    useEffect(() => {
      setIsConfigSidebarOpen(Boolean(configId))
      setIsParserSidebarOpen(Boolean(parserId))
    }, [configId, parserId])

    const closeDialogHandler = () => {
      setQueryParams({
        tab: INBOUND_TAB,
      })
      setIsConfigSidebarOpen(false)
      setIsParserSidebarOpen(false)
      history.push({
        pathname: `${EDI_TOOLS_ROUTES.SHIPPER_CONFIG}/${id}/shipperName/${name}`,
        search: tab ? `?tab=${tab}` : '',
      })
    }

    const showOnlyActiveChangeHandler = () => {
      setShowOnlyActive(!showOnlyActive)
    }

    const methods = useShipperConfigForm()

    const { control, errors } = methods

    const { selectOptions: transactionSetSelectOptions } = useTransactionSets()

    useEffectOnce(() => {
      transactionSetSelectOptions.push({ label: 'None', value: 'none' })
    })

    return (
      <div>
        {inboundMessageTypeLoading && <Spinner />}
        <Table dataTest='OverviewTable'>
          <TableHead>
            <>
              <Box pl={2} paddingY={1} display='flex' flexDirection='row' alignItems='center'>
                <Box>
                  <Switch
                    label='Show Only Active'
                    onChange={showOnlyActiveChangeHandler}
                    checked={showOnlyActive}
                  />
                </Box>
                <Box paddingLeft={2}>
                  <ControlledSelect
                    control={control}
                    defaultValue=''
                    name='transactionSetWithSubtype'
                    SelectProps={{
                      required: true,
                      label: 'EDI Transaction Set',
                      options: transactionSetSelectOptions,
                      large: true,
                      dataTest: 'edi-message-type',
                      error: !!errors?.transactionSet?.message,
                    }}
                    onChange={e => {
                      setSelectedTransactionSet(String(e.target.value))
                    }}
                  />
                </Box>
              </Box>
              <TableRow tableCellType='th'>
                <TableCell>Inbound Connection Config</TableCell>
                <TableCell>Config Status</TableCell>
                <TableCell>File Type</TableCell>
                <TableCell>Parser</TableCell>
                <TableCell>Parser Status</TableCell>
                <TableCell>Live Version ID</TableCell>
              </TableRow>
            </>
          </TableHead>
          <TableBody>
            {sortedEdiMessageTypes?.map((ediMessageType: IEdiMessage) => (
              <ShipperConfigInboundRow
                key={ediMessageType.id}
                ediMessageType={ediMessageType}
                onlyShowActive={showOnlyActive}
                selectedTransactionSet={selectedTransactionSet}
              />
            ))}
          </TableBody>
        </Table>
        {isParserSidebarOpen && <ParserSideBar onSidebarClose={closeDialogHandler} />}
        {isConfigSidebarOpen && (
          <InboundConnectionConfigDetails
            onEdit={submitHandler}
            onSidebarClose={closeDialogHandler}
          />
        )}
      </div>
    )
  }
)
